import { ChatCircle } from '@phosphor-icons/react';
import { getListApprovalsQueryKey } from '@reelplicate/api-client/approvals';
import {
  getListCommentsQueryKey,
  useCreateComment,
  useDeleteComment,
  useListComments,
  useMarkCommentsSeen,
  useUpdateComment,
} from '@reelplicate/api-client/comments';
import type { CommentDto } from '@reelplicate/api-client/model';
import { getGetNotificationUnreadCountQueryKey } from '@reelplicate/api-client/notifications';
import {
  Banner,
  Drawer,
  DrawerContent,
  EmptyState,
  Skeleton,
  Stack,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { CommentItem } from './comment-item';
import { MentionComposer } from './mention-composer';

/**
 * A draft's comments (Figma "Drawer Content / Comments" in a Wide Drawer):
 * the thread oldest first with one level of replies, and the comment box
 * pinned to the bottom. Opening it reads the draft's mentions and replies.
 */
export function CommentsDrawer({
  workspaceId,
  approvalId,
  draftTitle,
  flowName,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  approvalId: string;
  draftTitle?: string;
  flowName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        size="wide"
        title="Comments"
        description={[draftTitle, flowName].filter(Boolean).join(' · ') || undefined}
      >
        {open && <CommentThread workspaceId={workspaceId} approvalId={approvalId} />}
      </DrawerContent>
    </Drawer>
  );
}

/**
 * The thread itself - in the drawer, or beside a draft in its review modal.
 * Showing it reads the draft's mentions and replies.
 */
export function CommentThread({
  workspaceId,
  approvalId,
}: {
  workspaceId: string;
  approvalId: string;
}) {
  const queryClient = useQueryClient();
  const { data, isError, refetch } = useListComments(workspaceId, approvalId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false },
  });
  const create = useCreateComment();
  const update = useUpdateComment();
  const remove = useDeleteComment();
  const seen = useMarkCommentsSeen();
  const [replyTo, setReplyTo] = useState<CommentDto | null>(null);

  // The counts and the draft's "Mentioned" badge change with the thread.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getListCommentsQueryKey(workspaceId, approvalId),
      }),
      queryClient.invalidateQueries({ queryKey: getListApprovalsQueryKey(workspaceId) }),
      queryClient.invalidateQueries({
        queryKey: getGetNotificationUnreadCountQueryKey(workspaceId),
      }),
    ]);

  const { mutate: markSeen } = seen;
  useEffect(() => {
    if (!workspaceId) return;
    markSeen(
      { id: workspaceId, approvalId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getListApprovalsQueryKey(workspaceId),
          });
          void queryClient.invalidateQueries({
            queryKey: getGetNotificationUnreadCountQueryKey(workspaceId),
          });
        },
      },
    );
  }, [workspaceId, approvalId, markSeen, queryClient]);

  const items = data?.items ?? [];

  return (
    <div className="flex min-h-full flex-col gap-5">
      <div className="flex-1">
        {isError ? (
          <Banner
            tone="danger"
            title="Comments didn’t load"
            action={{ label: 'Try again', onClick: () => void refetch() }}
          />
        ) : !data ? (
          <Stack gap={5} aria-label="Loading comments">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14" shape="rectangle" />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ChatCircle}
            title="No comments yet"
            description="Ask a question or @mention a teammate — they’ll get a notification."
          />
        ) : (
          <Stack as="ol" gap={5} aria-label="Comments">
            {items.map((comment) => (
              <li key={comment.id}>
                <Stack gap={4}>
                  <CommentItem
                    comment={comment}
                    workspaceId={workspaceId}
                    onReply={() => setReplyTo(comment)}
                    onEdit={(body) => edit(comment.id, body)}
                    onDelete={() => destroy(comment.id)}
                  />
                  {comment.replies.length > 0 && (
                    <Stack as="ol" gap={4} aria-label="Replies" className="pl-11">
                      {comment.replies.map((reply) => (
                        <li key={reply.id}>
                          <CommentItem
                            comment={reply}
                            workspaceId={workspaceId}
                            isReply
                            onEdit={(body) => edit(reply.id, body)}
                            onDelete={() => destroy(reply.id)}
                          />
                        </li>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </li>
            ))}
          </Stack>
        )}
      </div>
      <div className="bg-bg-surface sticky bottom-0 pt-3">
        <MentionComposer
          key={replyTo?.id ?? 'new'}
          workspaceId={workspaceId}
          placeholder={replyTo ? 'Write a reply…' : 'Add a comment…'}
          replyingTo={replyTo?.author.name}
          onCancelReply={() => setReplyTo(null)}
          autoFocus={Boolean(replyTo)}
          onSubmit={async (body) => {
            await create.mutateAsync({
              id: workspaceId,
              approvalId,
              data: { body, ...(replyTo ? { parentId: replyTo.id } : {}) },
            });
            setReplyTo(null);
            await refresh();
          }}
        />
      </div>
    </div>
  );

  async function edit(commentId: string, body: string) {
    await update.mutateAsync({ id: workspaceId, commentId, data: { body } });
    await refresh();
  }

  async function destroy(commentId: string) {
    await remove.mutateAsync({ id: workspaceId, commentId });
    await refresh();
  }
}
